import React, { useState, type ReactNode, type HTMLAttributes } from "react";
import Box, { type BoxProps } from "@mui/material/Box";
import ToolTipComponent from "../tooltip";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode;
  className?: string;
  tooltipContent?: ReactNode;
  alwaysShowTooltip?: boolean;
  sx?: BoxProps["sx"];
  placement?: any;
  grid?: boolean;
}

const sxMapper: any = {
  grid: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 1,
  },
  normal: {
    display: "inline-block",
    maxWidth: "100%",
    whiteSpace: "nowrap",
  },
};

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style,
  sx,
  placement,
  grid = false,
  ...rest
}) => {
  const [tooltipTitle, setTooltipTitle] = useState<ReactNode | string>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const hasOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

    if (alwaysShowTooltip || hasOverflow) {
      setTooltipTitle(tooltipContent || String(children) || "");
    }
  };

  const handleMouseLeave = () => {
    setTooltipTitle("");
  };

  return (
    <ToolTipComponent content={tooltipTitle} position={placement ?? "top"}>
      <Box
        component="div"
        className={className}
        style={style}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          verticalAlign: "bottom",
          fontFamily: "inherit",
          ...sxMapper[grid ? "grid" : "normal"],
          ...sx,
        }}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
